import { getAccessToken, getScopeHeaders } from '../lib/auth-storage';

const API_BASE = import.meta.env.VITE_SUPPLIER_API ?? '/api/v1';

export type Supplier = {
  id: string;
  supplier_code: string;
  legal_name: string;
  category: string;
  status: string;
  health_score: number;
  risk_score: number;
  performance_score: number;
};

export type SupplierDashboard = {
  total_suppliers: number;
  active_suppliers: number;
  pending_approval: number;
  suspended: number;
  expiring_certs: number;
  high_risk: number;
  avg_health_score: number;
  open_contracts: number;
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getAccessToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...getScopeHeaders(),
      ...init?.headers,
    },
  });
  if (!res.ok) throw new Error(await res.text() || res.statusText);
  if (res.status === 204) return undefined as T;
  const json = await res.json();
  return (json.data ?? json) as T;
}

export const supplierApi = {
  dashboard: () => request<SupplierDashboard>('/suppliers/dashboard'),

  listSuppliers: () => request<Supplier[]>('/suppliers'),

  getSupplier: (id: string) => request<Supplier>(`/suppliers/${id}`),

  createSupplier: (body: { legal_name: string; category: string; country?: string; gstin?: string }) =>
    request<Supplier>('/suppliers', { method: 'POST', body: JSON.stringify(body) }),

  approveSupplier: (id: string) =>
    request<Supplier>(`/suppliers/${id}/approve`, { method: 'POST' }),

  activateSupplier: (id: string) =>
    request<Supplier>(`/suppliers/${id}/activate`, { method: 'POST' }),

  getPerformance: (id: string) => request<unknown>(`/suppliers/${id}/performance`),

  getRisk: (id: string) => request<unknown>(`/suppliers/${id}/risk`),

  listContracts: (id: string) => request<unknown[]>(`/suppliers/${id}/contracts`),

  analytics: () => request<{ total_suppliers: number; avg_performance: number; avg_risk: number }>('/suppliers/analytics'),

  intelligence: (id: string) => request<{ health_score: number; recommendations: string[] }>(`/suppliers/${id}/intelligence`),
};
